/**
 * Shared block/ban handling for every axios client.
 *
 * The backend rejects banned emails / email-domains / IPs with
 * `403 { error: 'user_banned' }` at the auth choke points (get-user-id on
 * login/signup, and the v2-api router). This installs a response interceptor
 * that, on that specific error, flips the global ban store (so AppShell shows
 * the suspended screen) and tears down the local session so a banned user can't
 * linger in an authed state. All other errors pass through untouched.
 */
import type { AxiosInstance } from 'axios';
import { useBanStore } from '@/store/banStore';
import { signOut as firebaseSignOut } from '@/auth/firebase';
import { clearAuthHintCookie } from '@/auth/authHintCookie';
import { clearSessionToken } from '@/auth/sessionToken';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isUserBannedError(e: any): boolean {
  if (e?.response?.status !== 403) return false;
  const err = e?.response?.data?.error;
  // get-user-id (v1 lambdas) returns { error: 'user_banned' }; the v2-api
  // envelope returns { error: { code: 'user_banned' } } — accept both.
  return err === 'user_banned' || err?.code === 'user_banned';
}

// Guard so multiple concurrent failing requests only tear the session down once.
let sessionTornDown = false;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function handleBanError(e: any): Promise<void> {
  if (!isUserBannedError(e)) return;
  const data = e?.response?.data || {};
  // Reason/message lives at the top level (v1) or inside the error object (v2).
  const reason = data.reason || data.message || data.error?.message;
  useBanStore.getState().setBanned(reason);
  if (sessionTornDown) return;
  sessionTornDown = true;
  // Best-effort session teardown — never let a cleanup failure mask the ban.
  try { await firebaseSignOut(); } catch { /* noop */ }
  try {
    clearAuthHintCookie();
    clearSessionToken();
    localStorage.removeItem('vivideo_user_id');
    localStorage.removeItem('vivideo_email_auth');
  } catch { /* noop */ }
}

/** True for the backend's 401 `auth_required`: the request carried no valid
 *  credential for the account it acted on (e.g. a pre-hardening session). */
export function isAuthRequiredError(status?: number, data?: { error?: unknown }): boolean {
  return status === 401 && data?.error === 'auth_required';
}

/** True for the backend's 403 `forbidden`: the login is valid but belongs to another
 *  account than the stored user id (e.g. a stale id after switching accounts). */
export function isAccountMismatchError(status?: number, data?: { error?: unknown }): boolean {
  return status === 403 && data?.error === 'forbidden';
}

let reauthStarted = false;

/** End the local session and send the user to sign in again (runs once). */
export async function endSessionForReauth(): Promise<void> {
  if (reauthStarted) return;
  reauthStarted = true;
  try { await firebaseSignOut(); } catch { /* noop */ }
  try {
    clearAuthHintCookie();
    clearSessionToken();
    localStorage.removeItem('vivideo_user_id');
    localStorage.removeItem('vivideo_email_auth');
  } catch { /* noop */ }
  if (!window.location.pathname.startsWith('/login')) window.location.assign('/login');
}

/** Attach the ban + sign-in-required response interceptor to an axios client. */
export function installBanInterceptor(client: AxiosInstance): void {
  client.interceptors.response.use(
    (r) => r,
    (e) => {
      void handleBanError(e);
      const status = e?.response?.status;
      const data = e?.response?.data;
      if (isAuthRequiredError(status, data) || isAccountMismatchError(status, data)) void endSessionForReauth();
      return Promise.reject(e);
    },
  );
}
